import React, { memo, useMemo } from 'react';

import * as C from './Charts.styled';
import { CHART_COMPARE_DASH, CHART_TONES } from './chartTokens';

export interface ISparklineGeometry {
  /** SVG-пути отрезков; `null` рвёт линию. */
  paths: string[];
  /** Последняя точка — для точки на конце. */
  last: { x: number; y: number } | null;
  /** Столбики (вариант `bars`): x, y, высота. */
  bars: Array<{ x: number; y: number; height: number; width: number }>;
}

const round = (value: number) => Math.round(value * 10) / 10;

/**
 * Геометрия спарклайна — чистая: тест без DOM. Ось Y — от нуля до максимума
 * (общего с `compare`, если он передан: иначе две линии в разных масштабах
 * врали бы о сравнении).
 */
export const sparklineGeometry = (
  values: ReadonlyArray<number | null>,
  {
    width = 80,
    height = 24,
    pad = 2,
    max: forcedMax,
  }: { width?: number; height?: number; pad?: number; max?: number } = {},
): ISparklineGeometry => {
  const known = values.filter((value): value is number => value !== null);
  const max = forcedMax ?? Math.max(0, ...known);
  const min = Math.min(0, ...known);
  const span = max - min || 1;
  const n = values.length;
  const step = n > 1 ? (width - pad * 2) / (n - 1) : 0;
  const xOf = (index: number) => round(pad + step * index);
  const yOf = (value: number) =>
    round(height - pad - ((value - min) / span) * (height - pad * 2));

  const paths: string[] = [];
  let current = '';
  let last: ISparklineGeometry['last'] = null;
  values.forEach((value, index) => {
    if (value === null) {
      if (current) paths.push(current);
      current = '';
      return;
    }
    const x = n === 1 ? round(width / 2) : xOf(index);
    const y = yOf(value);
    current += current ? ` L${x} ${y}` : `M${x} ${y}`;
    last = { x, y };
  });
  if (current) paths.push(current);

  const slot = n > 0 ? (width - pad * 2) / n : 0;
  const barWidth = Math.max(1, round(slot * 0.7));
  const bars = values.flatMap((value, index) => {
    if (value === null) return [];
    const y = yOf(Math.max(value, 0));
    return [
      {
        x: round(pad + slot * index + (slot - barWidth) / 2),
        y,
        height: round(height - pad - y),
        width: barWidth,
      },
    ];
  });

  return { paths, last, bars };
};

export interface ISparklineProps {
  values: ReadonlyArray<number | null>;
  /** Ряд сравнения — тонким пунктиром под основным. */
  compare?: ReadonlyArray<number | null> | null;
  color?: string;
  width?: number;
  height?: number;
  /** Линия (по умолчанию) или столбики. */
  variant?: 'line' | 'bars';
  /** Подпись для чтеца экрана: «Лиды за 14 дней: 3, 5, 4…». */
  ariaLabel?: string;
  /**
   * Столбики: нулевой день — бледной засечкой у основания (пакет 2): видно,
   * что дней 14 и пустые — пустые, а не что ряд из двух столбиков.
   */
  zeroStubs?: boolean;
}

/** Высота засечки нулевого столбика, px. */
const ZERO_STUB = 1.5;

/**
 * ТЗ 0103 — спарклайн на SVG без canvas: до 50 на таблицу, `null` рвёт
 * линию, мок jest не нужен. Декоративный, если подписи нет.
 */
export const Sparkline = memo(
  ({
    values,
    compare = null,
    color = CHART_TONES.accent,
    width = 80,
    height = 24,
    variant = 'line',
    ariaLabel,
    zeroStubs = false,
  }: ISparklineProps) => {
    const max = useMemo(
      () =>
        Math.max(
          0,
          ...values.filter((value): value is number => value !== null),
          ...(compare ?? []).filter((value): value is number => value !== null),
        ),
      [values, compare],
    );
    const main = useMemo(
      () => sparklineGeometry(values, { width, height, max }),
      [values, width, height, max],
    );
    const previous = useMemo(
      () =>
        compare ? sparklineGeometry(compare, { width, height, max }) : null,
      [compare, width, height, max],
    );
    return (
      <C.SparkSvg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
        focusable="false"
      >
        {previous?.paths.map((d) => (
          <path
            key={`p-${d}`}
            d={d}
            fill="none"
            stroke={color}
            strokeOpacity={0.45}
            strokeWidth={1}
            strokeDasharray={CHART_COMPARE_DASH.join(' ')}
          />
        ))}
        {variant === 'bars'
          ? main.bars.map((bar) =>
              bar.height <= 0 && zeroStubs ? (
                <rect
                  key={`b-${bar.x}`}
                  x={bar.x}
                  y={bar.y - ZERO_STUB}
                  width={bar.width}
                  height={ZERO_STUB}
                  fill={color}
                  fillOpacity={0.25}
                />
              ) : (
                <rect
                  key={`b-${bar.x}`}
                  x={bar.x}
                  y={bar.y}
                  width={bar.width}
                  height={bar.height}
                  fill={color}
                  rx={1}
                />
              ),
            )
          : main.paths.map((d) => (
              <path
                key={`c-${d}`}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
        {variant === 'line' && main.last && (
          <circle cx={main.last.x} cy={main.last.y} r={1.75} fill={color} />
        )}
      </C.SparkSvg>
    );
  },
);

Sparkline.displayName = 'Sparkline';
